import React from 'react';
import { MsalProvider, useMsal } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./auth/authConfig";
import AgentStatusDashboard from './components/AgentStatusDashboard';
import Login from './components/Login';
import './App.css';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promise and initialize
msalInstance.initialize().then(() => {
  // Handle redirect from Azure AD
  msalInstance.handleRedirectPromise().then((tokenResponse) => {
    if (tokenResponse !== null) {
      console.log('✅ Login successful!', tokenResponse);
      msalInstance.setActiveAccount(tokenResponse.account);
      console.log('✅ Active account set:', tokenResponse.account.username);
    } else {
      // Account selection logic
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        console.log('✅ Existing account found:', accounts[0].username);
      } else {
        console.log('ℹ️ No accounts found');
      }
    }
  }).catch((error) => {
    console.error('❌ Redirect error:', error);
  });

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
      console.log('✅ Login event - Account set:', account.username);
    }
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      console.log('✅ Logout successful');
    }
  });
});

function AppContent() {
  const { accounts, inProgress, instance } = useMsal();
  const [authChecked, setAuthChecked] = React.useState(false);
  
  // Check authentication on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const allAccounts = instance.getAllAccounts();
        console.log('🔍 Checking auth state:', {
          accounts: allAccounts,
          accountCount: allAccounts.length,
          inProgress,
          activeAccount: instance.getActiveAccount()
        });
        
        if (allAccounts.length > 0 && !instance.getActiveAccount()) {
          console.log('📌 Setting active account:', allAccounts[0].username);
          instance.setActiveAccount(allAccounts[0]);
        }
        
        setAuthChecked(true);
      } catch (error) {
        console.error('❌ Auth check error:', error);
        setAuthChecked(true);
      }
    };
    
    checkAuth();
  }, [instance, inProgress]);
  
  // Show loading while MSAL is processing or checking auth
  if (inProgress !== "none" || !authChecked) {
    return (
      <div className="App">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#fff'
        }}>
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(148, 163, 184, 0.2)',
            borderTopColor: '#DC143C',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '1rem', color: '#94a3b8' }}>
            {inProgress === "handleRedirect" ? 'Completing sign in...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }
  
  // Check if user is authenticated
  const isAuthenticated = accounts && accounts.length > 0;
  
  console.log('🎯 Final auth state:', { 
    isAuthenticated, 
    accountCount: accounts?.length, 
    inProgress,
    firstAccount: accounts?.[0]?.username 
  });
  
  const isEmbedMode = new URLSearchParams(window.location.search).get('embed') === 'true' 
    || window.self !== window.top;

  return (
    <div className={`App ${isEmbedMode ? 'embed-mode' : ''}`}>
      {isAuthenticated ? (
        <AgentStatusDashboard embedMode={isEmbedMode} />
      ) : (
        <Login />
      )}
    </div>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
}

export default App;
